'use client'

import { Box, Typography } from '@mui/material'
import type { RESOURCE_TYPE } from '@models'
import type { DefaultSearchCardsData } from '@query/feature-query/default-search-cards'
import {
  ActionButton,
  StandardCard,
  type StandardCardData,
} from '@components/common'
import { getStyles } from '@utils/styles'
import { SearchBlogCard } from './SearchBlogCard'
import { SearchCaseStudyCard } from './SearchCaseStudyCard'
import { SearchServiceCard } from './SearchServiceCard'
import defaultStyles from './styles'

export type SearchCardsVariant = 'standard' | 'service' | 'blog' | 'caseStudy'

type Props = {
  data: DefaultSearchCardsData
  sectionId?: string
  resourceType?: RESOURCE_TYPE
  truncateDescription?: boolean
  descriptionLineClamp?: number
  variant?: SearchCardsVariant
  /** CTA label for service-style cards (Services / Industries / Hire). */
  serviceCtaLabel?: string
}

function getListStyleName(variant: SearchCardsVariant) {
  if (variant === 'service') return 'serviceResultsList'
  if (variant === 'blog' || variant === 'caseStudy')
    return 'resourceResultsList'
  return 'resultsList'
}

function SearchResultCard({
  item,
  variant,
  resourceType,
  truncateDescription,
  descriptionLineClamp,
  serviceCtaLabel,
}: {
  item: StandardCardData
  variant: SearchCardsVariant
  resourceType?: RESOURCE_TYPE
  truncateDescription: boolean
  descriptionLineClamp?: number
  serviceCtaLabel?: string
}) {
  const styles = getStyles(defaultStyles)

  if (variant === 'service')
    return <SearchServiceCard {...item} ctaLabel={serviceCtaLabel} />
  if (variant === 'blog') return <SearchBlogCard {...item} />
  if (variant === 'caseStudy') return <SearchCaseStudyCard {...item} />

  return (
    <StandardCard
      {...item}
      resourceTypePage={resourceType}
      truncateDescription={truncateDescription}
      descriptionLineClamp={descriptionLineClamp}
      csx={{
        card: styles('card').sx,
        cardMedia: styles('image').sx,
      }}
    />
  )
}

export function DefaultSearchCards({
  data,
  sectionId,
  resourceType,
  truncateDescription = false,
  descriptionLineClamp,
  variant = 'standard',
  serviceCtaLabel,
}: Props) {
  const styles = getStyles(defaultStyles)
  const heading = data.sectionHeaderData?.heading
  const actionButtonData = data.sectionHeaderData?.actionButtonData
  const cards = data.cardsData ?? []

  if (!heading && !actionButtonData && cards.length === 0) {
    return null
  }

  return (
    <Box
      id={sectionId}
      {...styles(sectionId ? 'resultSection' : 'defaultCardsSection')}
    >
      {(heading || actionButtonData) && (
        <Box {...styles('defaultCardsHeader')}>
          {heading ? (
            <Typography variant="body1.700" {...styles('defaultCardsHeading')}>
              {heading}
            </Typography>
          ) : (
            <span />
          )}
          {actionButtonData ? (
            <ActionButton
              {...actionButtonData}
              variant="text"
              showArrow
              color="primary"
              noAnimation
              iconSx={{ color: 'primary.main !important' }}
              {...styles('viewAllButton')}
            />
          ) : null}
        </Box>
      )}

      {cards.length > 0 ? (
        <Box {...styles(getListStyleName(variant))}>
          {cards.map((item, idx) => (
            <Box
              key={`${item.title || 'result'}-${item.href || idx}-${idx}`}
              {...styles('resultItem')}
            >
              <SearchResultCard
                item={item}
                variant={variant}
                resourceType={resourceType}
                truncateDescription={truncateDescription}
                descriptionLineClamp={descriptionLineClamp}
                serviceCtaLabel={serviceCtaLabel}
              />
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  )
}
